import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loading } from '@carbon/react';
import { WarningAlt } from '@carbon/react/icons';
import BacklogPanel from 'src/components/Dashboard/BacklogPanel';
import BucketedBarChart from 'src/components/Dashboard/BucketedBarChart';
import CategoryBarChart from 'src/components/Dashboard/CategoryBarChart';
import StatCard from 'src/components/Dashboard/StatCard';
import { useDashboardIssueReports } from 'src/hooks/useDashboardReports';
import { useMoney } from 'src/hooks/useMoney';
import { getIssueTypeColor, getIssueTypeText, IssueStatuses } from 'src/utils/issue';
import { useNameLookups } from './useNameLookups';

interface IssuesProps {}

/**
 * The open backlog is a SNAPSHOT — an issue opened two months ago and still open is exactly
 * what a backlog is for — so its tile and list carry "as of today". Every chart follows the
 * range. Status and type labels are the existing `translation` keys from `src/utils/issue.ts`.
 */
const Issues = React.memo((_: IssuesProps) => {
  const { t } = useTranslation('dashboard');
  const {
    query: { isLoading, data: report, isRefetching },
    bucket,
  } = useDashboardIssueReports();
  const { employeeName, branchName } = useNameLookups();
  const { format, formatCompact } = useMoney();
  const asOfToday = `(${t('asOfToday')})`;
  const typeLabel = (type: number) => t(`translation:${getIssueTypeText(type)}`);
  // The server returns only the statuses that occur in the window; walk every status so a
  // zero reads as a zero rather than as a missing bar.
  const countByStatus: Record<number, number> = Object.fromEntries(
    (report?.statusFunnel || []).map((row) => [row.status, row.count])
  );

  return (
    <div className={'issues'}>
      {(isLoading || isRefetching) && <Loading small />}
      <div className="stats">
        <StatCard
          title={`${t('openBacklog')} ${asOfToday}`}
          value={report?.openBacklogTotal ?? 0}
          icon={<WarningAlt size={36} />}
        />
      </div>
      <div className="stats">
        <CategoryBarChart
          title={t('statusFunnel')}
          categoryAxisTitle={t('status')}
          valueAxisTitle={t('count')}
          data={IssueStatuses.map((status) => ({
            label: t(`translation:${status.label}`),
            value: countByStatus[status.value] ?? 0,
          }))}
        />
      </div>
      <div className="stats">
        <BucketedBarChart
          title={t('issueVolume')}
          dateFormat={bucket === 'month' ? 'MM/YYYY' : 'DD/MM'}
          data={(report?.volumeByBucket || []).map((row) => ({
            bucketKey: row.bucketKey,
            group: typeLabel(row.issueType),
            value: row.count,
          }))}
        />
      </div>
      <div className="stats">
        <CategoryBarChart
          title={t('finesByType')}
          categoryAxisTitle={t('issueType')}
          valueAxisTitle={t('fine')}
          data={(report?.finesByType || []).map((row) => ({
            label: typeLabel(row.issueType),
            value: row.fine,
          }))}
          axisValueFormatter={formatCompact}
          tooltipValueFormatter={format}
        />
      </div>
      <div className="stats">
        <CategoryBarChart
          title={t('finesByBranch')}
          categoryAxisTitle={t('branch')}
          valueAxisTitle={t('fine')}
          data={(report?.finesByBranch || []).map((row) => ({
            label: branchName(row.branchId),
            value: row.fine,
          }))}
          axisValueFormatter={formatCompact}
          tooltipValueFormatter={format}
        />
      </div>
      <div className="stats">
        <CategoryBarChart
          title={t('issuesByBranch')}
          categoryAxisTitle={t('branch')}
          valueAxisTitle={t('count')}
          data={(report?.issuesByBranch || []).map((row) => ({ label: branchName(row.branchId), value: row.count }))}
        />
      </div>
      <div className="stats">
        <CategoryBarChart
          title={t('mostIssues')}
          categoryAxisTitle={t('employee')}
          valueAxisTitle={t('count')}
          data={(report?.topByCount || []).map((row) => ({ label: employeeName(row.employeeId), value: row.count }))}
        />
      </div>
      <div className="stats">
        <CategoryBarChart
          title={t('topOffenders')}
          categoryAxisTitle={t('employee')}
          valueAxisTitle={t('fine')}
          data={(report?.topOffenders || []).map((row) => ({
            label: employeeName(row.employeeId),
            value: row.fine,
          }))}
          axisValueFormatter={formatCompact}
          tooltipValueFormatter={format}
        />
      </div>
      <div className="stats">
        {/* The ten oldest only — the tile above is the whole backlog, so the headings differ. */}
        <BacklogPanel
          title={`${t('oldestOpenIssues')} ${asOfToday}`}
          items={(report?.openBacklog || []).map((row) => ({
            id: row.id,
            title: row.title,
            label: typeLabel(row.type),
            tagType: getIssueTypeColor(row.type),
            createdAt: row.createdAt,
          }))}
        />
      </div>
    </div>
  );
});

export default Issues;
